import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Video, Building2, Calendar, User } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { useT } from "@/lib/i18n";

/**
 * Public playback page for a shared video cover letter.
 * URL: /video/:token — token is the `public_token` field on the video-letter.
 *
 * We deliberately do NOT surface the applicant's name or private CV data —
 * only what the applicant chose to say on camera + the vacancy context.
 *
 * Analytics:
 * - Per-visitor `viewer_id` in localStorage → the same person watching
 *   twice counts as ONE session (max_watched_s stays stable).
 * - Voluntary "Wie ben je?" form saves name + company against the
 *   session so the applicant sees who watched.
 * - Playback heartbeat every 5s sends the max currentTime seen back to
 *   the server, so scrub-backs don't deflate the metric.
 */
const VIEWER_ID_KEY = "jobgrid.pitch.viewerId";
const HEARTBEAT_INTERVAL_MS = 5000;

function ensureViewerId() {
  try {
    let v = localStorage.getItem(VIEWER_ID_KEY);
    if (v && v.length >= 8) return v;
  } catch { /* Safari private mode */ }
  const v = `v-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  try { localStorage.setItem(VIEWER_ID_KEY, v); } catch { /* ignore */ }
  return v;
}

export default function VideoLetterPublic() {
  const { token } = useParams();
  const { t } = useT();
  const [video, setVideo] = useState(null);
  const [err, setErr] = useState(null);
  const [viewerId] = useState(ensureViewerId);
  const [identity, setIdentity] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jobgrid.pitch.identity") || "{}"); }
    catch { return {}; }
  });
  const [savedIdentity, setSavedIdentity] = useState(false);
  const [nameDraft, setNameDraft] = useState(identity.name || "");
  const [companyDraft, setCompanyDraft] = useState(identity.company || "");
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const heartbeatRef = useRef(null);
  const completedRef = useRef(false);

  // Fetch metadata + mint a view session on mount.
  useEffect(() => {
    if (!token) return;
    let cancel = false;
    api.get(`/public/video-letters/${token}`)
      .then((r) => { if (!cancel) setVideo(r.data); })
      .catch(() => { if (!cancel) setErr("not_found"); });
    api.post(`/public/video-letters/${token}/view`, { viewer_id: viewerId })
      .catch(() => { /* non-blocking */ });
    // Auto-attach identity if the viewer already filled it in on a
    // previous pitch of the same applicant (or another one — same viewer).
    if (identity?.name || identity?.company) {
      api.post(`/public/video-letters/${token}/identify`, {
        viewer_id: viewerId,
        viewer_name: identity.name || "",
        viewer_company: identity.company || "",
      }).then(() => setSavedIdentity(true)).catch(() => {});
    }
    return () => { cancel = true; };
  }, [token, viewerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Heartbeat while playing — we don't listen to `timeupdate` (too noisy)
  // and instead poll `currentTime` every 5s. Only fires the server call
  // when the max watched has actually advanced.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !token) return;
    const onPlay = () => {
      if (heartbeatRef.current) return;
      heartbeatRef.current = setInterval(() => {
        const ct = v.currentTime || 0;
        if (ct > maxWatchedRef.current) {
          maxWatchedRef.current = ct;
          api.post(`/public/video-letters/${token}/heartbeat`, {
            viewer_id: viewerId,
            watched_s: ct,
            completed: completedRef.current,
          }).catch(() => {});
        }
      }, HEARTBEAT_INTERVAL_MS);
    };
    const onPause = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
    const onEnded = () => {
      completedRef.current = true;
      const ct = v.duration || v.currentTime || 0;
      api.post(`/public/video-letters/${token}/heartbeat`, {
        viewer_id: viewerId,
        watched_s: ct,
        completed: true,
      }).catch(() => {});
      onPause();
    };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
      onPause();
    };
  }, [video, token, viewerId]);

  const saveIdentity = async (e) => {
    e?.preventDefault?.();
    const name = nameDraft.trim();
    const company = companyDraft.trim();
    if (!name && !company) return;
    try {
      await api.post(`/public/video-letters/${token}/identify`, {
        viewer_id: viewerId,
        viewer_name: name,
        viewer_company: company,
      });
      const next = { name, company };
      setIdentity(next);
      setSavedIdentity(true);
      try { localStorage.setItem("jobgrid.pitch.identity", JSON.stringify(next)); } catch { /* ignore */ }
    } catch { /* non-blocking */ }
  };

  return (
    <div className="min-h-screen bg-[#F0EFEA]" data-testid="video-letter-public">
      <header className="px-6 sm:px-10 py-4 flex items-center justify-between border-b border-black/5 bg-[#F9F8F6]">
        <Logo size="md" showTagline={false} />
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500 hidden sm:inline">
          {t("vidPublicEyebrow")}
        </p>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        {err && (
          <div className="ed-card p-8 text-center" data-testid="video-letter-public-notfound">
            <p className="font-serif text-2xl">{t("vidPublicMissing")}</p>
            <p className="font-mono text-xs text-neutral-500 mt-2">{t("vidPublicMissingHint")}</p>
          </div>
        )}

        {video && (
          <>
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-2">
              <Video size={12} strokeWidth={1.5} /> {t("vidPublicEyebrow")}
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl mt-3" data-testid="video-letter-public-title">
              {t("vidPublicTitle")}
            </h1>

            {/* Voluntary identity — recruiters can leave their name/
                company so the applicant can see WHO watched their pitch.
                Collapses to a subtle confirmation once submitted. */}
            {!savedIdentity ? (
              <form
                onSubmit={saveIdentity}
                data-testid="video-letter-public-identify"
                className="mt-5 border border-amber-300 bg-amber-50/60 p-4 flex flex-col sm:flex-row gap-3 sm:items-end"
              >
                <div className="flex-1">
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-900 mb-2 inline-flex items-center gap-1.5">
                    <User size={11} strokeWidth={1.5} /> {t("vidPublicIdentifyTitle")}
                  </p>
                  <p className="text-xs text-neutral-700 mb-3">{t("vidPublicIdentifyHint")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder={t("vidPublicIdentifyNamePh")}
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      className="ed-input"
                      data-testid="video-letter-public-name"
                      maxLength={120}
                    />
                    <input
                      type="text"
                      placeholder={t("vidPublicIdentifyCompanyPh")}
                      value={companyDraft}
                      onChange={(e) => setCompanyDraft(e.target.value)}
                      className="ed-input"
                      data-testid="video-letter-public-company"
                      maxLength={140}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!nameDraft.trim() && !companyDraft.trim()}
                  data-testid="video-letter-public-identify-submit"
                  className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-amber-800 bg-amber-800 text-white hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2"
                >
                  {t("vidPublicIdentifyCta")}
                </button>
              </form>
            ) : (
              <div
                className="mt-5 border border-emerald-300 bg-emerald-50/60 p-3 flex items-center gap-2"
                data-testid="video-letter-public-identify-saved"
              >
                <User size={12} strokeWidth={1.5} className="text-emerald-700" />
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-emerald-800">
                  {t("vidPublicIdentifySaved", { name: identity.name || identity.company })}
                </p>
              </div>
            )}

            <div className="mt-6 bg-black overflow-hidden aspect-video">
              <video
                ref={videoRef}
                src={video.storage_url}
                controls
                autoPlay={false}
                className="w-full h-full object-contain"
                data-testid="video-letter-public-player"
              />
            </div>

            <div className="mt-6 ed-card p-5 space-y-2">
              {video.job_title && (
                <p className="font-serif text-xl leading-tight">{video.job_title}</p>
              )}
              {video.company && (
                <p className="text-sm text-neutral-700 inline-flex items-center gap-1.5">
                  <Building2 size={13} strokeWidth={1.5} /> {video.company}
                </p>
              )}
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 inline-flex items-center gap-1.5">
                <Calendar size={11} strokeWidth={1.5} />
                {video.created_at ? new Date(video.created_at).toLocaleDateString() : ""}
                {video.duration_s ? ` · ${Math.round(video.duration_s)}s` : ""}
              </p>
            </div>

            <p className="mt-8 text-xs text-neutral-500 font-mono text-center">
              {t("vidPublicFooter")}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
